import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import {
  BaseAlert,
  BaseButton,
  baseQueryKeys,
  BaseSkeletonStack,
  createFieldHelper,
  FormContainer,
  renderFieldHelperInput,
  useStandardToast
} from '@cks-internal/react-base';
import { FormOfPaymentFieldArray } from './components/formOfPaymentFieldArray';
import { editActivityFlexGap } from '../../../../features/activities/utils/consts';
import { TravellerDuplicateBookingWarning } from '../../../../features/activities/travellers/travellerDuplicateBookingWarning';
import {
  EditActivityViewModel,
  CreateActivityModel
} from '../../../../features/activities/editActivity/editActivityForm/schema/editActivitySchema';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useEditActivityFormFields } from '../../../../features/activities/editActivity/editActivityForm/useEditActivityFormFields';
import { HotelBookingType } from '../../../../globalModels/common/hotelBookingType';
import { faAdd } from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ApiEndpoints } from '../../../../constants/endpoints/apiEndpoints.ts';
import { AddNewHotelModal } from '../../hotelSearch/addHotel/AddNewHotelModal';
import { HotelSearchResultCombined } from '../../hotelSearch/types/hotelSearchResultCombined';
import { EntityNotesWarning } from '../../../../features/entityNotes/EntityNotesWarning';
import { EntityNoteQueryType } from '../../../../enums/common/EntityNoteQueryType';
import { EntityNoteType } from '../../../../enums/common/EntityNoteType';
import { useCompanyRelatedCompanyQuery } from '../../../../features/company/edit/apis/useCompanyRelatedCompanyQuery';
import { useCompanyApprovalConfigQuery } from '../../../../features/company/approvals/apis/useCompanyApprovalConfigQuery';
import { ApplicationRoles } from '../../../../constants/applicationRoles';
import { EditActivityGuestTravellerNamesField } from '../../editActivity/sendItinerary/components/GuestTravellerNamesInput';

type Props = {
  activityId: number | undefined;
  isDisabled: boolean;
  formMethods: UseFormReturn<Partial<CreateActivityModel>>;
  onSubmit?: (data: Partial<CreateActivityModel>) => void;
  data: EditActivityViewModel | undefined;
};

export const NewActivityForm = (props: Props) => {
  const fieldHelper = createFieldHelper<CreateActivityModel>(props.formMethods);
  const companyIdForApproval = useWatch({
    control: props.formMethods.control,
    name: 'CompanyId'
  });
  const companyApprovalConfigForField = useCompanyApprovalConfigQuery(companyIdForApproval ?? 0);
  const approvalEnabled =
    (companyApprovalConfigForField.data?.IsEnabledForObtBookings ?? false) ||
    (companyApprovalConfigForField.data?.IsEnabledForAgentBookings ?? false);

  const formFields = useEditActivityFormFields(
    props.activityId,
    fieldHelper as any,
    props.data,
    props.formMethods as any,
    true,
    undefined,
    false,
    approvalEnabled
  );

  // Add AssignedToUserId field for create form
  const assignedToUserField = fieldHelper
    .selectInputQueryItems('AssignedToUserId', {
      searchUrl: ApiEndpoints.selectOptions.usersInRoles([ApplicationRoles.reservations, ApplicationRoles.folio])
    })
    .isString()
    .withLabel('Assigned To')
    .withInputProps({ isClearable: true });

  const disclosure = useDisclosure();
  const toast = useStandardToast();
  const queryClient = useQueryClient();
  const [hotelBookingType, travellerIds, checkInDate, checkOutDate, preferredCityId, companyId] = useWatch({
    control: props.formMethods.control,
    name: ['HotelBookingType', 'TravelerIds', 'CheckInDate', 'CheckOutDate', 'PreferredCityId', 'CompanyId']
  });

  const companyRelatedCompanyQuery = useCompanyRelatedCompanyQuery(companyId);

  const updateActivityHotel = useCallback(async (result: HotelSearchResultCombined) => {
    await queryClient
      .invalidateQueries({
        queryKey: [
          ...baseQueryKeys.querySelectOptions(ApiEndpoints.selectOptions.hotels.hotelInCity(preferredCityId ?? 0))
        ],
        type: 'all'
      })
      .then(() => {
        props.formMethods.setValue('PreferredHotelId', result.HotelId);
      });
  }, []);

  if (!props.data && props.activityId) {
    return <BaseSkeletonStack />;
  }

  return (
    <>
      <FormContainer methods={props.formMethods} onSubmit={props.onSubmit} hideControls={true} hideDebugOnLocal={true}>
        <Flex w={'100%'} flexDir={'column'} gap={'4px'} paddingBottom={'4px'}>
          <Flex gap={editActivityFlexGap}>
            {renderFieldHelperInput('Priority', formFields as any)}
            {renderFieldHelperInput('AssignedToUserId', [assignedToUserField])}
          </Flex>

          <Flex gap={editActivityFlexGap}>{renderFieldHelperInput('CompanyId', formFields)}</Flex>

          {companyId && (
            <Flex gap={editActivityFlexGap}>
              <EntityNotesWarning
                entityIds={[companyId]}
                queryType={EntityNoteQueryType.Company}
                noteType={EntityNoteType.CodeRed}
              />
            </Flex>
          )}

          {companyId && (companyRelatedCompanyQuery.data?.RelatedCompanies?.length ?? 0) > 0 && (
            <Flex gap={'editActivityFlexGap'}>
              <Box w={'50%'}>{renderFieldHelperInput('CompanyConfirmationId', formFields)}</Box>
            </Flex>
          )}
          {companyRelatedCompanyQuery.data?.RelatedCompanyNotes && (
            <Flex gap={'editActivityFlexGap'}>
              <Box w={'50%'}>
                <BaseAlert>{companyRelatedCompanyQuery.data.RelatedCompanyNotes}</BaseAlert>
              </Box>
            </Flex>
          )}

          <Flex gap={editActivityFlexGap}>
            <Box w={'100%'}>
              <Flex gap={'8px'} flexGrow={'1 0 0'}>
                <Box w={'100%'}>{renderFieldHelperInput('TravelerIds', formFields)}</Box>
              </Flex>
            </Box>

            <Box w={'200px'}>
              {EditActivityGuestTravellerNamesField(props.formMethods.control, 'GuestTravelerNames').render()}
            </Box>
          </Flex>

          {Array.isArray(travellerIds) &&
            travellerIds.length > 0 &&
            (checkInDate?.length ?? 0) > 0 &&
            (checkOutDate?.length ?? 0) > 0 && (
              <Flex>
                <TravellerDuplicateBookingWarning
                  travellerIds={travellerIds}
                  checkInDate={checkInDate}
                  checkOutDate={checkOutDate}
                  activityId={props.activityId}
                />
              </Flex>
            )}

          {Array.isArray(travellerIds) && travellerIds.length > 0 && (
            <Flex gap={editActivityFlexGap}>
              <EntityNotesWarning
                queryType={EntityNoteQueryType.Contact}
                noteType={EntityNoteType.CodeRed}
                entityIds={travellerIds}
              />
            </Flex>
          )}

          {hotelBookingType != HotelBookingType.Personal && (
            <Flex gap={editActivityFlexGap}>
              {renderFieldHelperInput('RequestedById', formFields)}
              {renderFieldHelperInput('ApprovedBookerId', formFields)}
            </Flex>
          )}

          <Flex gap={editActivityFlexGap}>
            {renderFieldHelperInput('PreferredCityFreeText', formFields)}
            {renderFieldHelperInput('PreferredCityId', formFields)}
            {renderFieldHelperInput('PreferredHotelFreeText', formFields)}
            {renderFieldHelperInput('PreferredHotelId', formFields)}
            {preferredCityId && (
              <BaseButton onClick={disclosure.onOpen} size='xs' variant='outline' icon={faAdd}>
                New Hotel
              </BaseButton>
            )}
          </Flex>

          <Flex gap={editActivityFlexGap}>
            <Box w={'100%'}>{renderFieldHelperInput('SelectedDates', formFields)}</Box>
          </Flex>

          <Flex gap={editActivityFlexGap}>
            <Box w={'100%'}>{renderFieldHelperInput('AdditionalHotelNotes', formFields)}</Box>
          </Flex>

          <Flex gap={editActivityFlexGap}>
            {renderFieldHelperInput('ConfirmationVia', formFields)}
            {renderFieldHelperInput('AreaAmenities', formFields)}
          </Flex>

          <Flex gap={editActivityFlexGap}>
            {renderFieldHelperInput('EstimatedTimeOfArrival', formFields)}
            {renderFieldHelperInput('NumberOfRooms', formFields)}
          </Flex>
          <Flex gap={editActivityFlexGap}>
            {renderFieldHelperInput('PhoneNumber', formFields)}
            {renderFieldHelperInput('Email', formFields)}
          </Flex>
          <Flex gap={editActivityFlexGap}>{renderFieldHelperInput('Comments', formFields)}</Flex>

          <Flex gap={editActivityFlexGap}>{renderFieldHelperInput('ApproverNotes', formFields)}</Flex>

          <Flex>
            <FormOfPaymentFieldArray
              activityId={undefined}
              hotelBookingType={hotelBookingType}
              formMethods={props.formMethods}
              isDisabled={props.isDisabled}
              requireFormOfPayment={false}
            />
          </Flex>
        </Flex>
      </FormContainer>
      {disclosure.open && (
        <AddNewHotelModal
          activityId={props.activityId!}
          disclosure={disclosure}
          forQuote={true}
          onHotelAdded={async (model) => {
            toast.successToast(
              'Hotel Created',
              `The hotel: "${model.HotelInformation.HotelName}" has been created and a blank rate added to your selected rates.`
            );
            await updateActivityHotel(model).then(() => {
              disclosure.onClose();
            });
          }}
        />
      )}
    </>
  );
};
